/**
 * 下拉刷新。
 *
 * 这个功能不是为了好看：冰箱数据是两个人共享的，对方加了东西之后，
 * 自己这边需要一个符合直觉的动作把最新状态拉过来。
 *
 * 实现上最容易踩的两个坑：
 *   1. `touchmove` 必须是**非被动**监听才能 preventDefault，否则浏览器会同时滚页面，
 *      整个下拉过程会变成"页面在滚、内容也在动"的鬼畜效果。
 *      所以监听器只能显式 addEventListener，不能用 React 的 onTouchMove。
 *   2. 只有 `scrollTop <= 0` 且手势明确向下时才接管，否则会把普通滚动吃掉。
 *
 * 全程只改 transform 和 opacity。
 */

import { useEffect, useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

/** 拉过这个距离松手才触发刷新 */
const TRIGGER_DISTANCE = 68;
/** 阻尼系数，越大越跟手 */
const RESISTANCE = 0.52;
/** 下拉过程中内容最多被拉走多少 */
const MAX_PULL = 132;

export interface PullToRefreshOptions {
	scrollerRef: RefObject<HTMLElement>;
	/** 触发刷新，可以返回 Promise；返回前指示器会一直转 */
	onRefresh: () => Promise<unknown> | void;
}

export function usePullToRefresh({ scrollerRef, onRefresh }: PullToRefreshOptions): void {
	const refreshRef = useRef(onRefresh);
	refreshRef.current = onRefresh;

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller || prefersReducedMotion()) return;

		const indicator = scroller.querySelector<HTMLElement>('[data-pull-indicator]');
		if (!indicator) return;

		const indicatorY = gsap.quickSetter(indicator, 'y') as (value: number) => void;
		const indicatorOpacity = gsap.quickSetter(indicator, 'opacity') as (value: number) => void;
		const contentY = gsap.quickSetter(scroller, 'y') as (value: number) => void;

		let armed = false;
		let pulling = false;
		let refreshing = false;
		let startY = 0;
		let distance = 0;

		const reset = (): void => {
			pulling = false;
			distance = 0;
			gsap.to(scroller, {
				y: 0,
				duration: 0.42,
				ease: 'expo.out',
				overwrite: 'auto',
				onComplete: () => gsap.set(scroller, { clearProps: 'transform' }),
			});
			gsap.to(indicator, { y: 0, opacity: 0, duration: 0.34, ease: 'expo.out' });
		};

		const onTouchStart = (event: TouchEvent): void => {
			if (refreshing) return;
			// 只有已经在顶部时才可能下拉
			armed = scroller.scrollTop <= 0;
			const touch = event.touches[0];
			if (!touch) return;
			startY = touch.clientY;
		};

		const onTouchMove = (event: TouchEvent): void => {
			if (!armed || refreshing) return;
			const touch = event.touches[0];
			if (!touch) return;

			const raw = touch.clientY - startY;
			if (raw <= 0) {
				// 手指往上走，交还给正常滚动
				if (pulling) reset();
				return;
			}
			// 已经滚下去了就不再接管
			if (scroller.scrollTop > 0) {
				armed = false;
				return;
			}

			pulling = true;
			// 必须 preventDefault，否则页面会跟着一起滚
			if (event.cancelable) event.preventDefault();

			distance = Math.min(MAX_PULL, raw * RESISTANCE);

			contentY(distance);
			indicatorY(Math.min(TRIGGER_DISTANCE, distance) * 0.72);
			indicatorOpacity(Math.min(1, distance / TRIGGER_DISTANCE));
		};

		const onTouchEnd = (): void => {
			if (!pulling) {
				armed = false;
				return;
			}
			armed = false;

			if (distance < TRIGGER_DISTANCE) {
				reset();
				return;
			}

			refreshing = true;
			gsap.to(scroller, { y: TRIGGER_DISTANCE, duration: 0.3, ease: 'power2.out' });
			gsap.to(indicator, {
				y: TRIGGER_DISTANCE * 0.72,
				opacity: 1,
				duration: 0.24,
				ease: 'power2.out',
			});

			Promise.resolve(refreshRef.current()).finally(() => {
				refreshing = false;
				reset();
			});
		};

		scroller.addEventListener('touchstart', onTouchStart, { passive: true });
		// 非被动：下拉过程中要能阻止默认滚动
		scroller.addEventListener('touchmove', onTouchMove, { passive: false });
		scroller.addEventListener('touchend', onTouchEnd, { passive: true });
		scroller.addEventListener('touchcancel', onTouchEnd, { passive: true });

		return () => {
			scroller.removeEventListener('touchstart', onTouchStart);
			scroller.removeEventListener('touchmove', onTouchMove);
			scroller.removeEventListener('touchend', onTouchEnd);
			scroller.removeEventListener('touchcancel', onTouchEnd);
			gsap.killTweensOf(scroller);
			gsap.killTweensOf(indicator);
		};
	}, [scrollerRef]);
}
